import * as React from 'react';
import { useCallback, useState } from 'react';

import { Viewport, ViewLarge } from 'bernie-view';

import { UitkSheet, UitkSheetFooter } from 'uitk-react-sheet';

import { useStores } from 'utility/hooks';
import { trackAnalytics } from 'utility';
import { FlightAction, FlightsStandardOffer } from '__generated__/api';

import { DetailsAndFares, DetailsAndFaresFooter } from '.';

interface DetailsAndFaresSheetProps {
  onSheetClose?: (event?) => void;
  openForcedChoiceModal?: boolean;
  handleOfferSelect?: (selectOfferAction: FlightAction.Fragment) => void;
  showSummaryAndDetails: boolean;
  isDetailsVisible?: boolean;
  itemIndex?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableFaresInformation?: any;
  hygieneAmenitiesPresentation?: FlightsStandardOffer.HygieneAmenitiesPresentation;
}

export const DetailsAndFaresSheet = ({
  onSheetClose,
  openForcedChoiceModal,
  handleOfferSelect,
  showSummaryAndDetails,
  isDetailsVisible,
  itemIndex,
  availableFaresInformation,
  hygieneAmenitiesPresentation
}: DetailsAndFaresSheetProps) => {
  const {
    routeStore,
    dynamicElementsStore,
    priceSummaryStore,
    flightsSelectedJourneyReviewStore,
    analytics: analyticsStore
  } = useStores();
  const {
    fareChoiceInformation,
    dismissDetailedJourneyInformation: { icon },
    openAccessibility
  } = availableFaresInformation;
  const fareTypes = fareChoiceInformation ? fareChoiceInformation.fareTypes : null;
  const initialFareTypeSelectedIndex = fareTypes ? fareTypes.findIndex(fare => fare.selected) : 0;
  const selectedFareType = fareTypes ? fareTypes.find(fare => fare.selected) : null;
  const [footerInformation, setFooterInformation] = useState(selectedFareType);
  const iconLabel = icon.description;

  const setSelected = useCallback(
    (index: number) => {
      if (fareTypes) {
        const selectedFareType = fareTypes.find(fareType => fareType.selected);
        if (selectedFareType) {
          selectedFareType.selected = false;
        }
        fareTypes[index].selected = true;
        setFooterInformation(fareTypes[index]);
      }
    },
    [fareTypes]
  );

  const trackCloseAnalytics = () => {
    const {
      dismissDetailedJourneyInformation: { analytics }
    } = availableFaresInformation;
    trackAnalytics(analytics, analyticsStore);
  };

  const closeSheet = (event?: React.KeyboardEvent<HTMLButtonElement>) => {
    setSelected(initialFareTypeSelectedIndex);
    trackCloseAnalytics();
    onSheetClose && onSheetClose(event);
  };

  const updateFlightInformation = (identifier: string) => {
    flightsSelectedJourneyReviewStore.updateInformation(
      itemIndex,
      identifier,
      fareTypes,
      dynamicElementsStore,
      priceSummaryStore,
      routeStore
    );
  };

  const onApplySelectSheet = (identifier: string) => {
    onSheetClose && onSheetClose();
    updateFlightInformation(identifier);
  };

  const onApplySelectWithFareSelection = (identifier: string) => {
    fareTypes.find(fareType => fareType.selected).selected = false;
    fareTypes.find(fareType => fareType.identifier == identifier).selected = true;
    onApplySelectSheet(identifier);
  };

  return (
    <Viewport>
      <ViewLarge>
        {isDetailsVisible && (
          <div data-test-id="listing-details-and-fares">
            <UitkSheet
              isVisible={true}
              closeButtonAction={closeSheet}
              liveAnnounceString={openAccessibility}
              layout={{ position: 'right', columns: fareTypes ? (fareTypes.length === 1 ? 4 : 6) : 4 }}
              accessibleHeading={iconLabel}
            >
              <DetailsAndFares
                hygieneAmenitiesPresentation={hygieneAmenitiesPresentation}
                showSummaryAndDetails={showSummaryAndDetails}
                itemIndex={itemIndex}
                availableFaresInformation={availableFaresInformation}
                setSelected={setSelected}
                useOwnFooter={false}
                isSmallViewport={false}
              />
              {(footerInformation || availableFaresInformation) && (
                <UitkSheetFooter>
                  <DetailsAndFaresFooter
                    openForcedChoiceModal={openForcedChoiceModal}
                    onApplySelect={onApplySelectWithFareSelection}
                    handleOfferSelect={handleOfferSelect}
                    availableFaresInformation={availableFaresInformation}
                    isSmallViewport={false}
                  />
                </UitkSheetFooter>
              )}
            </UitkSheet>
          </div>
        )}
      </ViewLarge>
    </Viewport>
  );
};
